import {
    afterNextRender,
    effect,
    ElementRef,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    Injector,
    model,
    output,
    viewChild,
} from '@angular/core';
import { form, FormField, disabled } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NgIcon } from '@ng-icons/core';
import { QpxButton, QpxIcon, QpxTooltip } from '../../directives';
import { Loader } from '../loader/loader';

export type DataTableViewMode = 'list' | 'grid';
type QpxPaginationButtonAppearance = 'elevated' | 'outlined' | 'text';
export type QpxPaginationAppearance = QpxPaginationButtonAppearance;

type PaginationKind = 'unresolved' | 'invalid' | 'empty' | 'overflow' | 'ready';

interface PaginationState {
    readonly kind: PaginationKind;
    readonly currentPage: number;
    readonly totalPages: number;
    readonly statusLabel: string;
    readonly canGoPrevious: boolean;
    readonly canGoNext: boolean;
}

@Component({
    selector: 'qpx-pagination',
    imports: [
        FormField,
        MatButtonModule,
        MatButtonToggleModule,
        NgIcon,
        QpxButton,
        QpxIcon,
        QpxTooltip,
        Loader,
    ],
    templateUrl: './pagination.html',
    styleUrl: './pagination.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-appearance]': 'appearance()',
        '(focusin)': 'rememberBoundaryFocus($event)',
        '(focusout)': 'clearBoundaryFocus($event)',
    },
})
export class QpxPagination {
    //#region Dependency injection
    private readonly injector = inject(Injector);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    //#endregion

    //#region Inputs
    public readonly isRefetching = input(false);
    public readonly isLoading = input(false);
    public readonly loadFailed = input(false);
    public readonly ariaLabelledBy = input('');
    public readonly totalRecords = input.required<number>();
    public readonly pageSize = input.required<number>();
    public readonly pageNumber = input.required<number>();
    public readonly appearance = input<QpxPaginationAppearance>('text');
    public readonly viewMode = model<DataTableViewMode | undefined>(undefined);
    public readonly disabled = input(false);
    public readonly compact = input<boolean>();
    //#endregion

    protected readonly viewModeField = form(this.viewMode, (path) => {
        disabled(path, () => this.disabled());
    });

    //#region Outputs
    public readonly pageRequested = output<number>();
    //#endregion

    //#region Queries
    private readonly previousPageButton =
        viewChild<HTMLButtonElement>('previousPageButton');
    private readonly nextPageButton =
        viewChild<HTMLButtonElement>('nextPageButton');
    private readonly paginationNavigation = viewChild<ElementRef<HTMLElement>>(
        'paginationNavigation'
    );
    //#endregion

    //#region State
    protected readonly boundaryDisplay = computed(() => {
        const compact = this.compact();
        if (compact === undefined) return null;
        return compact ? 'none' : 'inline-flex';
    });
    protected readonly pagination = computed<PaginationState>(() =>
        resolvePagination(
            this.totalRecords(),
            this.pageSize(),
            this.pageNumber()
        )
    );
    protected readonly showPagination = computed(
        () =>
            !this.isLoading() &&
            ['empty', 'overflow', 'ready'].includes(this.pagination().kind)
    );
    protected readonly announcedStatus = computed(() => {
        const pagination = this.pagination();

        if (this.isLoading()) return '';

        if (['unresolved', 'invalid'].includes(pagination.kind)) {
            return this.isRefetching()
                ? 'Loading results'
                : this.loadFailed()
                  ? 'Unable to load results'
                  : '';
        }

        return this.isRefetching()
            ? `Loading page ${pagination.currentPage}`
            : this.loadFailed()
              ? `Unable to load page ${pagination.currentPage}`
              : pagination.statusLabel;
    });
    //#endregion

    private focusedBoundary: HTMLButtonElement | undefined;

    constructor() {
        effect(() => {
            if (!this.compact()) return;
            const host = this.host.nativeElement;
            const active = host.ownerDocument.activeElement;
            const focused = this.focusedBoundary;
            if (
                !focused ||
                !host.contains(focused) ||
                (active !== focused && active !== host.ownerDocument.body)
            ) {
                return;
            }
            const navigation = this.paginationNavigation()?.nativeElement;
            const target = navigation?.querySelector<HTMLButtonElement>(
                'button:not(.boundary-page):not(:disabled)'
            );
            (target ?? navigation)?.focus({
                preventScroll: true,
            });
        });
    }

    protected rememberBoundaryFocus(event: FocusEvent): void {
        const target = event.target;
        this.focusedBoundary =
            target instanceof HTMLButtonElement &&
            target.classList.contains('boundary-page')
                ? target
                : undefined;
    }

    protected clearBoundaryFocus(event: FocusEvent): void {
        if (!this.compact() || event.relatedTarget !== null) {
            this.focusedBoundary = undefined;
        }
    }

    //#region Template methods
    protected requestPage(targetPage: number): void {
        const pagination = this.pagination();

        if (
            this.disabled() ||
            !['overflow', 'ready'].includes(pagination.kind) ||
            !Number.isInteger(targetPage) ||
            targetPage < 1 ||
            targetPage > pagination.totalPages ||
            targetPage === pagination.currentPage
        ) {
            return;
        }

        this.preserveBoundaryFocus(targetPage, pagination.totalPages);
        this.pageRequested.emit(targetPage);
    }

    protected requestPreviousPage(): void {
        const pagination = this.pagination();

        this.requestPage(
            Math.min(pagination.currentPage - 1, pagination.totalPages)
        );
    }

    protected requestNextPage(): void {
        const pagination = this.pagination();

        this.requestPage(pagination.currentPage + 1);
    }

    private preserveBoundaryFocus(
        targetPage: number,
        totalPages: number
    ): void {
        if (totalPages <= 1) {
            afterNextRender(
                () => this.paginationNavigation()?.nativeElement.focus(),
                { injector: this.injector }
            );
            return;
        }

        const focusTarget =
            targetPage === 1
                ? this.nextPageButton
                : targetPage === totalPages
                  ? this.previousPageButton
                  : undefined;

        if (!focusTarget) return;

        afterNextRender(() => focusTarget()?.focus(), {
            injector: this.injector,
        });
    }
    //#endregion
}

function resolvePagination(
    totalRecords: number,
    pageSize: number,
    pageNumber: number
): PaginationState {
    if (totalRecords === 0 && pageSize === 0 && pageNumber === 0) {
        return unavailableState('unresolved');
    }

    if (
        !Number.isFinite(totalRecords) ||
        !Number.isFinite(pageSize) ||
        !Number.isFinite(pageNumber) ||
        !Number.isInteger(totalRecords) ||
        !Number.isInteger(pageSize) ||
        !Number.isInteger(pageNumber) ||
        totalRecords < 0 ||
        pageSize <= 0 ||
        pageNumber < 1
    ) {
        return unavailableState('invalid');
    }

    if (totalRecords === 0) {
        return pageNumber === 1
            ? {
                  kind: 'empty',
                  currentPage: 1,
                  totalPages: 0,
                  statusLabel: '0 results',
                  canGoPrevious: false,
                  canGoNext: false,
              }
            : unavailableState('invalid');
    }

    const totalPages = Math.ceil(totalRecords / pageSize);

    if (pageNumber > totalPages) {
        return {
            kind: 'overflow',
            currentPage: pageNumber,
            totalPages,
            statusLabel: `Page ${pageNumber} is unavailable; ${totalRecords} total ${totalRecords === 1 ? 'result' : 'results'}, ${totalPages} total ${totalPages === 1 ? 'page' : 'pages'}`,
            canGoPrevious: true,
            canGoNext: false,
        };
    }

    return {
        kind: 'ready',
        currentPage: pageNumber,
        totalPages,
        statusLabel: `Showing ${(pageNumber - 1) * pageSize + 1}–${Math.min(
            pageNumber * pageSize,
            totalRecords
        )} of ${totalRecords} results, page ${pageNumber} of ${totalPages}`,
        canGoPrevious: pageNumber > 1,
        canGoNext: pageNumber < totalPages,
    };
}

function unavailableState(
    kind: Extract<PaginationKind, 'unresolved' | 'invalid'>
): PaginationState {
    return {
        kind,
        currentPage: 0,
        totalPages: 0,
        statusLabel: '',
        canGoPrevious: false,
        canGoNext: false,
    };
}
